/**
 * Party Organisation API Contract
 * 
 * This contract defines the API interface for organisation operations.
 * It lives in the domain package to maintain proper dependency direction.
 * 
 * Platform → Domains (never reverse)
 */

import { z } from 'zod';

// ============================================================================
// Core Schemas
// ============================================================================

/**
 * Organisation type enumeration
 */
export const OrganisationTypeSchema = z.enum([
  'Corporation',
  'LLC',
  'Partnership',
  'SoleProprietorship',
  'NonProfit',
  'Government',
  'Other'
]);

/**
 * Organisation size categories
 */
export const OrganisationSizeSchema = z.enum([
  'Micro',      // 1-9 employees
  'Small',      // 10-49 employees
  'Medium',     // 50-249 employees
  'Large',      // 250-999 employees
  'Enterprise'  // 1000+ employees
]);

/**
 * Core organisation schema - unified data structure
 */
export const OrganisationSchema = z.object({
  // Identity
  partyId: z.string().uuid(),
  
  // Names - simplified from complex DDD structure
  name: z.string().min(1),              // Display name (computed)
  legalName: z.string().min(1),         // Official registered name
  tradingName: z.string().optional(),    // DBA/Trading as
  
  // Classification
  organisationType: OrganisationTypeSchema,
  organisationSize: OrganisationSizeSchema,
  industry: z.string().optional(),
  
  // Details
  description: z.string().optional(),
  website: z.string().url().optional(),
  contactEmail: z.string().email().optional(),
  foundedYear: z.number().int().min(1800).max(new Date().getFullYear()).optional(),
  
  // Location
  headquarters: z.string().optional(),
  
  // Metrics
  employeeCount: z.number().int().min(0).optional(),
  revenue: z.number().min(0).optional(),
  stockSymbol: z.string().optional(),
  
  // Relationships
  parentOrganisationId: z.string().uuid().optional(),
  parentOrganisationName: z.string().optional(),
  subsidiaryCount: z.number().int().min(0).default(0),
  
  // Status
  isActive: z.boolean().default(true),
  
  // Timestamps
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * Organisation list item - optimized for list views
 */
export const OrganisationListItemSchema = OrganisationSchema.pick({
  partyId: true,
  name: true,
  legalName: true,
  organisationType: true,
  organisationSize: true,
  industry: true,
  isActive: true,
  headquarters: true,
  employeeCount: true,
  createdAt: true,
  updatedAt: true,
});

/**
 * Organisation detail view - includes computed fields
 */
export const OrganisationDetailSchema = OrganisationSchema.extend({
  // Employee statistics
  employeeStats: z.object({
    totalEmployees: z.number().int().min(0),
    recentHires: z.number().int().min(0),
    departmentBreakdown: z.array(z.object({
      department: z.string(),
      count: z.number().int().min(0),
    })),
  }).optional(),
  
  // Data quality indicator
  dataCompleteness: z.object({
    percentage: z.number().min(0).max(100),
    missingFields: z.array(z.string()),
  }).optional(),
});

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Search criteria for filtering organisations
 */
export const OrganisationSearchCriteriaSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(10),
  
  // Filters
  searchTerm: z.string().optional(),
  organisationType: OrganisationTypeSchema.optional(),
  organisationSize: OrganisationSizeSchema.optional(),
  industry: z.string().optional(),
  isActive: z.boolean().optional(),
  parentOrganisationId: z.string().uuid().optional(),
  
  // Sorting
  sortBy: z.enum(['name', 'legalName', 'createdAt', 'updatedAt', 'employeeCount']).default('name'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

/**
 * Organisation list response
 */
export const OrganisationListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(OrganisationListItemSchema),
  metadata: z.object({
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    hasMore: z.boolean(),
  }),
  timestamp: z.string().datetime(),
});

/**
 * Single organisation response
 */
export const OrganisationDetailResponseSchema = z.object({
  success: z.boolean(),
  data: OrganisationDetailSchema,
  timestamp: z.string().datetime(),
});

/**
 * Create organisation request
 */
export const CreateOrganisationRequestSchema = z.object({
  legalName: z.string().min(1),
  tradingName: z.string().optional(),
  organisationType: OrganisationTypeSchema,
  organisationSize: OrganisationSizeSchema,
  industry: z.string().optional(),
  description: z.string().optional(),
  website: z.string().url().optional(),
  contactEmail: z.string().email().optional(),
  foundedYear: z.number().int().min(1800).max(new Date().getFullYear()).optional(),
  headquarters: z.string().optional(),
  employeeCount: z.number().int().min(0).optional(),
});

/**
 * Update organisation request
 */
export const UpdateOrganisationRequestSchema = CreateOrganisationRequestSchema.partial();

/**
 * Error response schema
 */
export const OrganisationErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.any().optional(),
  }),
  timestamp: z.string().datetime(),
});

// ============================================================================
// Contract Definition
// ============================================================================

/**
 * Complete organisation API contract
 */
export const OrganisationContract = {
  /**
   * List organisations with filtering and pagination
   */
  list: {
    method: 'GET' as const,
    path: '/api/party/organisations',
    queryParams: OrganisationSearchCriteriaSchema,
    response: OrganisationListResponseSchema,
    errorResponse: OrganisationErrorResponseSchema,
  },
  
  /**
   * Get single organisation by ID
   */
  get: {
    method: 'GET' as const,
    path: '/api/party/organisations/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    response: OrganisationDetailResponseSchema,
    errorResponse: OrganisationErrorResponseSchema,
  },
  
  /**
   * Create new organisation
   */
  create: {
    method: 'POST' as const,
    path: '/api/party/organisations',
    body: CreateOrganisationRequestSchema,
    response: OrganisationDetailResponseSchema,
    errorResponse: OrganisationErrorResponseSchema,
  },
  
  /**
   * Update existing organisation
   */
  update: {
    method: 'PUT' as const,
    path: '/api/party/organisations/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    body: UpdateOrganisationRequestSchema,
    response: OrganisationDetailResponseSchema,
    errorResponse: OrganisationErrorResponseSchema,
  },
  
  /**
   * Delete organisation (soft delete)
   */
  delete: {
    method: 'DELETE' as const,
    path: '/api/party/organisations/:partyId',
    params: z.object({ partyId: z.string().uuid() }),
    response: z.object({
      success: z.boolean(),
      message: z.string(),
      timestamp: z.string().datetime(),
    }),
    errorResponse: OrganisationErrorResponseSchema,
  },
};

// ============================================================================
// Type Exports
// ============================================================================

export type Organisation = z.infer<typeof OrganisationSchema>;
export type OrganisationListItem = z.infer<typeof OrganisationListItemSchema>;
export type OrganisationDetail = z.infer<typeof OrganisationDetailSchema>;
export type OrganisationSearchCriteria = z.infer<typeof OrganisationSearchCriteriaSchema>;
export type OrganisationListResponse = z.infer<typeof OrganisationListResponseSchema>;
export type OrganisationDetailResponse = z.infer<typeof OrganisationDetailResponseSchema>;
export type CreateOrganisationRequest = z.infer<typeof CreateOrganisationRequestSchema>;
export type UpdateOrganisationRequest = z.infer<typeof UpdateOrganisationRequestSchema>;
export type OrganisationErrorResponse = z.infer<typeof OrganisationErrorResponseSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate organisation list response
 */
export function validateOrganisationListResponse(data: unknown): data is OrganisationListResponse {
  return OrganisationListResponseSchema.safeParse(data).success;
}

/**
 * Validate organisation detail response
 */
export function validateOrganisationDetailResponse(data: unknown): data is OrganisationDetailResponse {
  return OrganisationDetailResponseSchema.safeParse(data).success;
}

/**
 * Validate and parse organisation list response
 */
export function parseOrganisationListResponse(data: unknown): OrganisationListResponse {
  return OrganisationListResponseSchema.parse(data);
}

/**
 * Validate and parse organisation detail response
 */
export function parseOrganisationDetailResponse(data: unknown): OrganisationDetailResponse {
  return OrganisationDetailResponseSchema.parse(data);
}